import { PostData } from "./FetchData";
import { formatAuditIdForApi, isValidAuditId } from "../utils/auditIdUtils";

const MS1 = "ms1";

const POLL_MS = 2500;
const LIST_POLL_MS = 5000;

function parseMs1Response(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "failure") {
		const message =
			typeof res.message === "string" ? res.message : fallbackMessage;
		return { ok: false, message, data: res.data ?? null };
	}
	if (!isSuccess(res)) {
		const message =
			typeof res?.message === "string"
				? res.message
				: res?.message?.message || fallbackMessage;
		return { ok: false, message, data: null };
	}
	return { ok: true, message: res.message ?? null, data: res.data ?? null };
}

function isSuccess(res) {
	return res !== "Invalid" && res !== undefined && res?.status === "success";
}

export function normalizeTaskStatus(raw) {
	if (!raw || typeof raw !== "object") {
		return { progress: 0, status: "pending", result: null, error: null };
	}
	const progress = Math.min(100, Math.max(0, Number(raw.progress ?? 0)));
	const status = String(raw.status ?? "pending").toLowerCase();
	const errField = raw.error_message ?? raw.error;
	const error =
		errField != null && errField !== ""
			? typeof errField === "string"
				? errField
				: JSON.stringify(errField)
			: null;
	return {
		progress: Number.isFinite(progress) ? progress : 0,
		status,
		result: raw.result ?? null,
		error,
	};
}

export function isTaskComplete(progress, status) {
	const s = String(status || "").toLowerCase();
	return progress >= 100 || s === "completed" || s === "success";
}

export function isTaskFailed(status, error) {
	const s = String(status || "").toLowerCase();
	if (s === "failed" || s === "error" || s === "cancelled") return true;
	return error != null && error !== "";
}

export async function saveAiVisibilityStore(storeURL, storeType, options = {}) {
	const body = {
		storeURL: (storeURL || "").trim(),
		enable_web_search: options.enable_web_search ?? false,
		prompt_count: Number(options.prompt_count ?? 15),
	};
	if (storeType) body.storeType = storeType;
	const res = await PostData(MS1, "api/v1/website/saveAiVisibilityStore", body);
	const parsed = parseMs1Response(res, "Failed to save store");
	if (!parsed.ok) return { ...parsed, taskStatus: null };
	const data = parsed.data ?? {};
	const taskStatus = normalizeTaskStatus(data.audit?.taskStatus ?? data.taskStatus);
	return { ok: true, message: parsed.message, data, taskStatus };
}

export async function getAiVisibilityTaskStatus(websiteId) {
	if (websiteId == null || websiteId === "") {
		return { ok: false, message: "Missing website id", ...normalizeTaskStatus(null) };
	}
	const res = await PostData(MS1, "api/v1/website/aiVisibilityTaskStatus", {
		websiteId: Number(websiteId),
	});
	const parsed = parseMs1Response(res, "Failed to load task status");
	if (!parsed.ok) return { ok: false, message: parsed.message, ...normalizeTaskStatus(null) };
	const ts = normalizeTaskStatus(parsed.data?.taskStatus ?? parsed.data);
	return { ok: true, message: null, ...ts, raw: parsed.data };
}

export async function listAiVisibilityAudits(websiteId, skip = 0, limit = 20) {
	const res = await PostData(MS1, "api/v1/website/aiVisibilityAudits", {
		websiteId: websiteId != null ? Number(websiteId) : undefined,
		skip,
		limit,
	});
	const parsed = parseMs1Response(res, "Failed to load audit history");
	if (!parsed.ok) return { ok: false, message: parsed.message, audits: [], total: 0, hasMore: false };
	const audits = Array.isArray(parsed.data) ? parsed.data : parsed.data?.audits ?? [];
	const total =
		parsed.data != null && !Array.isArray(parsed.data) && parsed.data.total != null
			? Number(parsed.data.total)
			: audits.length;
	return {
		ok: true,
		message: parsed.message,
		audits,
		total,
		hasMore: skip + audits.length < total,
	};
}

export async function getAiVisibilityHighlights(websiteId, auditId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", highlights: null };
	}
	if (!isValidAuditId(auditId)) {
		return { ok: false, message: "Missing audit id", highlights: null };
	}
	const res = await PostData(MS1, "api/v1/website/aiVisibilityHighlights", {
		websiteId: Number(websiteId),
		auditId: formatAuditIdForApi(auditId),
	});
	const parsed = parseMs1Response(res, "Failed to load crawl highlights");
	if (!parsed.ok) return { ok: false, message: parsed.message, highlights: null };
	return { ok: true, message: parsed.message, highlights: parsed.data };
}

export async function getAiVisibilityReport(websiteId, auditId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", report: null };
	}
	const body = { websiteId: Number(websiteId) };
	const formattedAuditId = formatAuditIdForApi(auditId);
	if (formattedAuditId != null) {
		body.auditId = formattedAuditId;
	}
	const res = await PostData(MS1, "api/v1/website/aiVisibilityReport", body);
	const parsed = parseMs1Response(res, "Failed to load report");
	if (!parsed.ok) return { ok: false, message: parsed.message, report: null };
	return { ok: true, message: parsed.message, report: parsed.data };
}

export async function rerunAiVisibilityAudit(websiteId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", audit: null, taskStatus: null };
	}
	const res = await PostData(MS1, "api/v1/website/aiVisibilityRerun", {
		websiteId: Number(websiteId),
	});
	const parsed = parseMs1Response(res, "Failed to rerun audit");
	if (!parsed.ok) return { ok: false, message: parsed.message, audit: null, taskStatus: null };
	const taskStatus = normalizeTaskStatus(parsed.data?.taskStatus);
	return {
		ok: true,
		message: parsed.message,
		audit: parsed.data?.audit ?? null,
		taskStatus,
	};
}

export async function cancelAiVisibilityAudit(websiteId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", audit: null };
	}
	const res = await PostData(MS1, "api/v1/website/aiVisibilityCancel", {
		websiteId: Number(websiteId),
	});
	const parsed = parseMs1Response(res, "Failed to cancel audit");
	if (!parsed.ok) return { ok: false, message: parsed.message, audit: null };
	return { ok: true, message: parsed.message, audit: parsed.data };
}

function countCitedProviders(results) {
	if (!results || typeof results !== "object") return 0;
	return ["openai", "gemini", "claude"].filter((key) => {
		const r = results[key] ?? results[`${key}_result`];
		return r && (r.cited === true || r.cited === 1);
	}).length;
}

function countCitedFromProviderResults(raw) {
	if (!raw || typeof raw !== "object") return 0;
	return ["openai_result", "gemini_result", "claude_result"].filter((key) => {
		const r = raw[key];
		return r && (r.cited === true || r.cited === 1);
	}).length;
}

export function normalizeCustomPromptItem(raw) {
	if (!raw || typeof raw !== "object") return null;
	const id = raw.id ?? raw.questionId ?? raw.question_id;
	if (id == null) return null;

	const providerCount =
		Number(raw.provider_count ?? raw.providerCount ?? raw.total_providers) || 3;
	let citedCount =
		raw.citation_count ?? raw.citationCount ?? raw.cited_count ?? raw.citedCount;
	if (citedCount == null) {
		citedCount = countCitedFromProviderResults(raw);
		if (!citedCount) {
			citedCount = countCitedProviders(raw.results ?? raw.provider_results);
		}
	}

	return {
		id: Number(id),
		question: String(raw.question ?? raw.prompt ?? "").trim(),
		createdAt: raw.created_at ?? raw.createdAt ?? raw.date ?? null,
		citedCount: Number(citedCount) || 0,
		providerCount,
		queryType: raw.query_type ?? raw.queryType ?? "custom",
		openai_result: raw.openai_result ?? null,
		gemini_result: raw.gemini_result ?? null,
		claude_result: raw.claude_result ?? null,
		websearch_results: raw.websearch_results ?? null,
	};
}

export function normalizeCustomPromptList(data) {
	const list = Array.isArray(data)
		? data
		: data?.prompts ?? data?.questions ?? data?.items ?? [];
	return list.map(normalizeCustomPromptItem).filter(Boolean);
}

export function normalizeCitationSummary(data) {
	if (!data || typeof data !== "object") {
		return {
			updatedCitationRate: 0,
			customPromptCount: undefined,
			totalPrompts: 0,
			totalCitations: 0,
			perProvider: { openai: 0, gemini: 0, claude: 0 },
		};
	}

	const perProviderRaw =
		data.per_provider ?? data.perProvider ?? data.provider_rates ?? data.providers ?? {};

	const pickRate = (topLevelKey, nestedKeys) => {
		const topLevel = data[topLevelKey];
		if (topLevel != null && Number.isFinite(Number(topLevel))) {
			return Number(topLevel);
		}
		for (const key of nestedKeys) {
			const val = perProviderRaw[key];
			if (val != null && Number.isFinite(Number(val))) return Number(val);
		}
		return 0;
	};

	const customPromptCountRaw =
		data.custom_prompt_count ?? data.customPromptCount ?? data.custom_count;

	return {
		updatedCitationRate: Number(
			data.overall_citation_rate ??
				data.updated_citation_rate ??
				data.updatedCitationRate ??
				data.citation_rate ??
				data.rate ??
				0
		),
		customPromptCount:
			customPromptCountRaw != null ? Number(customPromptCountRaw) : undefined,
		totalPrompts: Number(
			data.total_questions ??
				data.total_prompts ??
				data.totalPrompts ??
				data.total ??
				0
		),
		totalCitations: Number(
			data.total_citations ?? data.totalCitations ?? data.citations ?? 0
		),
		perProvider: {
			openai: pickRate("openai_rate", ["openai", "chatgpt", "ChatGPT"]),
			gemini: pickRate("gemini_rate", ["gemini", "Gemini"]),
			claude: pickRate("claude_rate", ["claude", "Claude"]),
		},
	};
}

function customPromptBody(websiteId, auditId) {
	return {
		websiteId: Number(websiteId),
		auditId: formatAuditIdForApi(auditId),
	};
}

export async function createAiVisibilityCustomPrompt(websiteId, auditId, question) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", prompt: null };
	}
	if (!isValidAuditId(auditId)) {
		return { ok: false, message: "Missing audit id", prompt: null };
	}
	const trimmed = String(question ?? "").trim();
	if (!trimmed) {
		return { ok: false, message: "Prompt cannot be empty", prompt: null };
	}

	const res = await PostData(MS1, "api/v1/website/aiVisibilityCustomPromptCreate", {
		...customPromptBody(websiteId, auditId),
		question: trimmed,
		query_type: "custom",
	});
	const parsed = parseMs1Response(res, "Failed to create custom prompt");
	if (!parsed.ok) return { ok: false, message: parsed.message, prompt: null };
	return {
		ok: true,
		message: parsed.message,
		prompt: normalizeCustomPromptItem(parsed.data?.prompt ?? parsed.data),
	};
}

export async function listAiVisibilityCustomPrompts(websiteId, auditId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", prompts: [] };
	}
	if (!isValidAuditId(auditId)) {
		return { ok: false, message: "Missing audit id", prompts: [] };
	}

	const res = await PostData(MS1, "api/v1/website/aiVisibilityCustomPrompts", customPromptBody(websiteId, auditId));
	const parsed = parseMs1Response(res, "Failed to load custom prompts");
	if (!parsed.ok) return { ok: false, message: parsed.message, prompts: [] };
	return {
		ok: true,
		message: parsed.message,
		prompts: normalizeCustomPromptList(parsed.data),
	};
}

export async function getAiVisibilityCitationSummary(websiteId, auditId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id", summary: null };
	}
	if (!isValidAuditId(auditId)) {
		return { ok: false, message: "Missing audit id", summary: null };
	}

	const res = await PostData(MS1, "api/v1/website/aiVisibilityCitationSummary", customPromptBody(websiteId, auditId));
	const parsed = parseMs1Response(res, "Failed to load citation summary");
	if (!parsed.ok) return { ok: false, message: parsed.message, summary: null };
	return {
		ok: true,
		message: parsed.message,
		summary: normalizeCitationSummary(parsed.data),
	};
}

export async function deleteAiVisibilityCustomPrompt(websiteId, auditId, questionId) {
	if (websiteId == null) {
		return { ok: false, message: "Missing website id" };
	}
	if (!isValidAuditId(auditId)) {
		return { ok: false, message: "Missing audit id" };
	}
	if (questionId == null) {
		return { ok: false, message: "Missing question id" };
	}

	const res = await PostData(MS1, "api/v1/website/aiVisibilityCustomPromptDelete", {
		...customPromptBody(websiteId, auditId),
		questionId: Number(questionId),
	});
	const parsed = parseMs1Response(res, "Failed to delete custom prompt");
	if (!parsed.ok) return { ok: false, message: parsed.message };
	return { ok: true, message: parsed.message };
}

export { POLL_MS, LIST_POLL_MS };
